import { useState, useRef, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useFormContext, useFieldArray, useWatch } from "react-hook-form";
import type { QuoteSummary, ProcessesedRows } from "../types/models";
import { Box, Typography, Stack, styled, Accordion, AccordionDetails, AccordionSummary } from "~/common/mui";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import Tag from "~/common/components/Tag/Tag";
import { AlterQuoteQuantity, QuotePreviewLineItem } from "./QuotePreviewLineItem";
import { type QuotePreviewFormValues } from "./QuoteSplitDialog";
import { NumberFormatter as NF } from "~/common/utils/NumberFormatter";
import { useCommerceContext } from "~/app/config/useSession";

interface QuotePreviewCardProps {
  actualQuoteIndex: number;
  actualQuoteDetails: QuoteSummary;
  confirmedQtyMap: Record<number, number>;
  positionOptions: Map<string, number[] | undefined>;
  materialNumberOptions: (string | undefined)[];
}

export const QuotePreviewItem = ({
  actualQuoteIndex,
  actualQuoteDetails,
  confirmedQtyMap,
  positionOptions,
  materialNumberOptions,
}: QuotePreviewCardProps) => {
  const { t } = useTranslation();
  const alterQuoteQuantityPermission = useCommerceContext()?.alterQuoteQuantity ?? AlterQuoteQuantity.DENY_ALL;
  const [expanded, setExpanded] = useState(true);

  const { quoteNumber } = actualQuoteDetails;

  const {
    control,
    formState: { errors },
    getValues,
    trigger,
  } = useFormContext<QuotePreviewFormValues>();

  const {
    fields: lineItemsFieldArray,
    update: updateItem,
    remove: removeItem,
  } = useFieldArray<QuotePreviewFormValues, `rows.${number}.items`>({
    control,
    name: `rows.${actualQuoteIndex}.items` as const,
  });

  const lineItems = useWatch({
    name: `rows.${actualQuoteIndex}.items`,
  }) as ProcessesedRows[];

  const lineItemsPrevLength = useRef(lineItems.length);

  const triggerAllQuantities = useCallback(async () => {
    const items = getValues(`rows.${actualQuoteIndex}.items`) ?? [];

    for (let i = 0; i < items.length; i++) {
      await trigger(`rows.${actualQuoteIndex}.items.${i}.quantity`);
    }
  }, [actualQuoteIndex, getValues, trigger]);

  useEffect(() => {
    if (lineItems.length < lineItemsPrevLength.current) {
      triggerAllQuantities();
    }
    lineItemsPrevLength.current = lineItems.length;
  }, [lineItems, triggerAllQuantities]);

  const totalLineItemsQty = lineItems.reduce((sum, q) => sum + (q.quantity || 0), 0);

  const totalLineItemsQtyFromActualQuote = () => {
    const visibleconfirmedQtyMap = new Map();

    lineItems.forEach((row) => {
      const key = `${row.position}+${row.product}`;
      const qty = confirmedQtyMap?.[row.position] ?? 0;
      visibleconfirmedQtyMap.set(key, qty);
    });

    let total = 0;
    for (const value of visibleconfirmedQtyMap.values()) {
      total += value;
    }

    return total;
  };

  const getTotalQtyOfRepeatedMaterials = useCallback(
    (position: number, materialNumber: string, id: number) => {
      return lineItems
        .filter((row) => row.position === position && row.product === materialNumber)
        .reduce((total, q) => (id === q.lineItemId ? total : total + (q.quantity || 0)), 0);
    },
    [lineItems],
  );

  const checkAndUpdatePosition = async (index: number, update: ProcessesedRows) => {
    const currentRow = getValues(`rows.${actualQuoteIndex}.items.${index}`);

    const productPositionOptions = positionOptions.get(update.product) ?? [];

    let newPosition = currentRow.position;

    if (productPositionOptions.length === 1) {
      newPosition = productPositionOptions[0];
    }

    if (productPositionOptions.length > 1) {
      newPosition = 0;
    }

    updateItem(index, {
      ...currentRow,
      product: update.product,
      position: newPosition,
    });
    await trigger(`rows.${actualQuoteIndex}.items.${index}.position`);
  };

  const highlightError =
    Array.isArray(errors.rows?.[actualQuoteIndex]?.items) && errors.rows[actualQuoteIndex]!.items!.length > 0;

  if (lineItemsFieldArray.length == 0) {
    return null;
  }

  const isWarningVisible =
    totalLineItemsQty < totalLineItemsQtyFromActualQuote() &&
    alterQuoteQuantityPermission == AlterQuoteQuantity.ALLOW_ALL;

  return (
    <Container defaultExpanded highlightError={highlightError}>
      <StyledAccordionSummary onClick={() => setExpanded(!expanded)}>
        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
          <Stack direction="row" gap={1} sx={{ alignItems: "center" }}>
            <Typography variant="bodyLarge" color="textPrimary">
              {t("quotation.split.preview.quote")}
            </Typography>
            <Typography variant="bodyLarge" color="textPrimary" component={"b"}>
              {quoteNumber}
            </Typography>

            {!expanded && (
              <Typography color="textSecondary">
                {t("quotation.split.preview.lintItems.split.message", { splittedRows: lineItems.length })}
              </Typography>
            )}

            {highlightError ? (
              <Tag variant="error" icon={<WarningAmberOutlinedIcon fontSize="small" />} iconPosition="end">
                {t("quotation.split.preview.lintItems.split.tagMessage", {
                  qty: NF.getDefaultFormatter().format(totalLineItemsQty),
                  totalQty: NF.getDefaultFormatter().format(totalLineItemsQtyFromActualQuote()),
                })}
              </Tag>
            ) : isWarningVisible ? (
              <Tag variant="warn" icon={<WarningAmberOutlinedIcon fontSize="small" />} iconPosition="end">
                {t("quotation.split.preview.lintItems.split.tagMessage", {
                  qty: NF.getDefaultFormatter().format(totalLineItemsQty),
                  totalQty: NF.getDefaultFormatter().format(totalLineItemsQtyFromActualQuote()),
                })}
              </Tag>
            ) : (
              <Tag variant="success" icon={<CheckCircleOutlineIcon fontSize="small" />} iconPosition="end">
                {t("quotation.split.preview.lintItems.split.tagMessage", {
                  qty: NF.getDefaultFormatter().format(totalLineItemsQty),
                  totalQty: NF.getDefaultFormatter().format(totalLineItemsQtyFromActualQuote()),
                })}
              </Tag>
            )}
          </Stack>
        </Box>
      </StyledAccordionSummary>
      <StyledAccordionDetails highlightError={highlightError}>
        <Stack gap={3}>
          {lineItemsFieldArray.map((row, index) => {
            const productKey = lineItems[index].product;
            const productPositionOptions = positionOptions.get(productKey) ?? [];

            return (
              <QuotePreviewLineItem
                key={row.lineItemId}
                index={index}
                lineItemInfo={lineItems[index]}
                actualQuoteIndex={actualQuoteIndex}
                quotedQtyForLineItem={confirmedQtyMap?.[lineItems[index].position]}
                positionOptions={productPositionOptions}
                materialNumberOptions={materialNumberOptions}
                getTotalQtyOfRepeatedMaterials={getTotalQtyOfRepeatedMaterials}
                checkAndUpdatePosition={checkAndUpdatePosition}
                removeItem={removeItem}
              />
            );
          })}
        </Stack>
      </StyledAccordionDetails>
    </Container>
  );
};

const Container = styled(Accordion, {
  shouldForwardProp: (prop) => prop !== "highlightError",
})<{ highlightError: boolean }>(({ theme, highlightError }) => ({
  border: `1px solid ${highlightError ? theme.palette.error.main : theme.palette.divider}`,
  borderRadius: "12px",
  background: theme.palette.surface.bright,
  marginBottom: "12px !important",
  "&::before": {
    opacity: 0,
  },
  "&.Mui-expanded::before": {
    opacity: 0,
  },
  "&::after": {
    opacity: 0,
  },
  "&.Mui-expanded::after": {
    opacity: 0,
  },
}));

const StyledAccordionDetails = styled(AccordionDetails, {
  shouldForwardProp: (prop) => prop !== "highlightError",
})<{ highlightError: boolean }>(({ theme, highlightError }) => ({
  borderTop: `1px solid ${highlightError ? theme.palette.error.main : theme.palette.divider}`,
  padding: "24px 24px 12px 24px",
}));

const StyledAccordionSummary = styled(AccordionSummary)({
  fontWeight: "400",
  height: "48px",
  padding: "0 24px",
});
